import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Linking, Platform, Pressable, ScrollView, StyleSheet, View, type ViewStyle } from "react-native";
import { MotiView, AnimatePresence } from "moti";
import {
  ArrowSquareOutIcon,
  CaretDownIcon,
  CaretRightIcon,
  CheckCircleIcon,
  DownloadSimpleIcon,
  PaperPlaneTiltIcon,
  StackSimpleIcon,
  WarningIcon,
  XIcon,
} from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { ExternalVisibility } from "@axio/graphql/generated/graphql";
import { colors } from "../../../../../styles/design-system";
import type {
  PublishedPublishItem,
  PublishDocumentUpdate,
  PublishManualStep,
  PublishStatus,
  QueuedPublishItem,
} from "../types/reviewTypes";
import { getManagedFileDownloadAction, submitAuthenticatedHiddenDownloadForm } from "../utils/webFileDownload";

const SHEET_WIDTH = 440;

type PublishTab = "published" | "manual" | "documents";

interface PublishSheetProps {
  open: boolean;
  items: QueuedPublishItem[];
  /** Items that were published in the most recent run, shown in the success state. */
  publishedItems: PublishedPublishItem[];
  /** Items from the most recent run that require manual LMS work. */
  manualSteps: PublishManualStep[];
  /** Documents from analysis that need manual LMS updates. */
  documentUpdates: PublishDocumentUpdate[];
  /** Number of items still undecided after publishing, shown in the footer. */
  undecidedCount: number;
  lmsLabel: string;
  canPublishToLms: boolean;
  status: PublishStatus;
  onClose: () => void;
  onPublish: () => void;
  onExportPdf: () => void;
  onExportImscc: () => void;
  isExportingPdf?: boolean;
  isExportingImscc?: boolean;
  /** Dismisses the success state and closes the sheet. */
  onDone: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type PublishDisplayItem = {
  id: string;
  title: string;
  category: string;
  moduleId?: string | null;
  moduleName?: string | null;
  wasEdited?: boolean;
};

type PublishDisplayGroup = {
  moduleId: string | null;
  moduleName: string;
  items: PublishDisplayItem[];
};

function groupItemsByModule(items: PublishDisplayItem[]): PublishDisplayGroup[] {
  const groups: PublishDisplayGroup[] = [];
  const moduleIndex = new Map<string, number>();

  for (const item of items) {
    const moduleName = item.moduleName ?? "Course-Level Content";
    const key = item.moduleId ?? moduleName;
    const existingIdx = moduleIndex.get(key);
    if (existingIdx != null) {
      groups[existingIdx].items.push(item);
    } else {
      moduleIndex.set(key, groups.length);
      groups.push({
        moduleId: item.moduleId ?? null,
        moduleName,
        items: [item],
      });
    }
  }

  return groups;
}

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? `${count} ${singular}` : `${count} ${plural}`;
}

function sanitizeDownloadFileName(value: string): string {
  const sanitized = value
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

  return sanitized || "manual-step";
}

function getManualStepText(step: PublishManualStep, lmsLabel: string): string {
  return [
    step.action,
    "",
    `Item: ${step.title}`,
    `LMS: ${lmsLabel}`,
    "",
    step.detail,
    step.externalUrl ? `Open in ${lmsLabel}: ${step.externalUrl}` : null,
  ]
    .filter((line): line is string => line !== null)
    .join("\n");
}

function downloadManualStepText(step: PublishManualStep, lmsLabel: string): void {
  if (Platform.OS !== "web" || typeof document === "undefined" || typeof Blob === "undefined") {
    return;
  }

  const blob = new Blob([getManualStepText(step, lmsLabel)], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${sanitizeDownloadFileName(step.title)}-manual-step.txt`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function getDocumentUpdateText(documentUpdate: PublishDocumentUpdate, lmsLabel: string): string {
  return [
    "Document Update Instructions",
    "",
    `Document: ${documentUpdate.title}`,
    `LMS: ${lmsLabel}`,
    "",
    documentUpdate.reason,
    "Make the changes described above, then re-upload the updated document to the course.",
    documentUpdate.externalUrl ? `Open in ${lmsLabel}: ${documentUpdate.externalUrl}` : null,
  ]
    .filter((line): line is string => line !== null)
    .join("\n");
}

function downloadDocumentUpdateText(documentUpdate: PublishDocumentUpdate, lmsLabel: string): void {
  if (Platform.OS !== "web" || typeof document === "undefined" || typeof Blob === "undefined") {
    return;
  }

  const blob = new Blob([getDocumentUpdateText(documentUpdate, lmsLabel)], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${sanitizeDownloadFileName(documentUpdate.title)}-update-instructions.txt`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function PublishSheet({
  open,
  items,
  publishedItems,
  manualSteps,
  documentUpdates,
  undecidedCount,
  lmsLabel,
  canPublishToLms,
  status,
  onClose,
  onPublish,
  onExportPdf,
  onExportImscc,
  isExportingPdf = false,
  isExportingImscc = false,
  onDone,
}: PublishSheetProps) {
  const [collapsedModules, setCollapsedModules] = useState<Set<string>>(() => new Set());
  const [activeTab, setActiveTab] = useState<PublishTab>("published");
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [manualDownloadIds, setManualDownloadIds] = useState<Set<string>>(() => new Set());
  const [documentDownloadIds, setDocumentDownloadIds] = useState<Set<string>>(() => new Set());
  const tabInitializedRef = useRef(false);
  const manualDownloadIdsRef = useRef<Set<string>>(new Set());
  const documentDownloadIdsRef = useRef<Set<string>>(new Set());

  const isPublishing = status === "publishing";
  const isDone = status === "done";
  const isError = status === "error";
  const autoPublishedItems = publishedItems;

  // Show the auto-published list in the success state, otherwise show the queued items.
  const displayItems = isDone ? autoPublishedItems : items;
  const moduleGroups = useMemo(() => groupItemsByModule(displayItems), [displayItems]);

  // Any queued item with live visibility triggers the stronger student-impact warning.
  const hasLiveItems = useMemo(
    () => items.some((item) => item.externalVisibility === ExternalVisibility.Published),
    [items]
  );

  // Reset collapsed state so the first group is expanded when the items change
  useEffect(() => {
    if (moduleGroups.length === 0) return;
    const collapsed = new Set<string>();
    moduleGroups.slice(1).forEach((group) => {
      collapsed.add(group.moduleId ?? "__course_level__");
    });
    setCollapsedModules(collapsed);
  }, [moduleGroups]);

  useEffect(() => {
    if (!open || !isDone) {
      tabInitializedRef.current = false;
      setActiveTab("published");
      manualDownloadIdsRef.current.clear();
      documentDownloadIdsRef.current.clear();
      setManualDownloadIds(new Set());
      setDocumentDownloadIds(new Set());
      return;
    }

    if (!tabInitializedRef.current) {
      tabInitializedRef.current = true;
      setActiveTab(manualSteps.length > 0 ? "manual" : "published");
    }
  }, [isDone, manualSteps.length, open]);

  useEffect(() => {
    if (!open) {
      setIsExportMenuOpen(false);
    }
  }, [open]);

  const toggleModule = useCallback((moduleId: string | null) => {
    const key = moduleId ?? "__course_level__";
    setCollapsedModules((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handlePublish = useCallback(() => {
    if (items.length === 0) return;
    onPublish();
  }, [items.length, onPublish]);

  const handleOpenUrl = useCallback((url?: string | null) => {
    if (!url) return;
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    void Linking.openURL(url);
  }, []);

  const handleDownloadManualStep = useCallback(
    async (step: PublishManualStep) => {
      if (manualDownloadIdsRef.current.has(step.id)) {
        return;
      }

      manualDownloadIdsRef.current.add(step.id);
      setManualDownloadIds(new Set(manualDownloadIdsRef.current));

      try {
        if (step.downloadUrl) {
          const managedFileAction = getManagedFileDownloadAction(step.downloadUrl);
          if (managedFileAction) {
            await submitAuthenticatedHiddenDownloadForm(managedFileAction, `manual-step-download-${Date.now()}`, {
              disposition: "attachment",
            });
          } else {
            handleOpenUrl(step.downloadUrl);
          }
        } else {
          downloadManualStepText(step, lmsLabel);
        }
      } catch {
        // Keep the UI responsive if the browser blocks the download flow.
      }

      setTimeout(() => {
        manualDownloadIdsRef.current.delete(step.id);
        setManualDownloadIds(new Set(manualDownloadIdsRef.current));
      }, 800);
    },
    [handleOpenUrl, lmsLabel]
  );

  const handleDownloadDocumentUpdate = useCallback(
    async (documentUpdate: PublishDocumentUpdate) => {
      if (documentDownloadIdsRef.current.has(documentUpdate.id)) {
        return;
      }

      documentDownloadIdsRef.current.add(documentUpdate.id);
      setDocumentDownloadIds(new Set(documentDownloadIdsRef.current));

      try {
        if (documentUpdate.downloadUrl) {
          const managedFileAction = getManagedFileDownloadAction(documentUpdate.downloadUrl);
          if (managedFileAction) {
            await submitAuthenticatedHiddenDownloadForm(managedFileAction, `document-update-download-${Date.now()}`, {
              disposition: "attachment",
            });
          } else {
            handleOpenUrl(documentUpdate.downloadUrl);
          }
        } else {
          downloadDocumentUpdateText(documentUpdate, lmsLabel);
        }
      } catch {
        // Keep the UI responsive if the browser blocks the download flow.
      }

      setTimeout(() => {
        documentDownloadIdsRef.current.delete(documentUpdate.id);
        setDocumentDownloadIds(new Set(documentDownloadIdsRef.current));
      }, 800);
    },
    [handleOpenUrl, lmsLabel]
  );

  const canPublish = canPublishToLms && !isPublishing && items.length > 0;
  const isAnyExporting = isExportingPdf || isExportingImscc;
  const publishedDescription = `${pluralize(autoPublishedItems.length, "change", "changes")} published to ${lmsLabel}.${
    manualSteps.length > 0
      ? ` ${pluralize(manualSteps.length, "item", "items")} ${
          manualSteps.length === 1 ? "requires" : "require"
        } manual action.`
      : ""
  }${
    documentUpdates.length > 0
      ? ` ${pluralize(documentUpdates.length, "document", "documents")} ${
          documentUpdates.length === 1 ? "needs" : "need"
        } updating.`
      : ""
  }`;
  const tablistLabel = "Published changes tabs";

  return (
    <AnimatePresence>
      {open && (
        <MotiView
          key="publish-sheet-overlay"
          from={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: "timing", duration: 150 }}
          style={styles.overlay}>
          <Pressable style={styles.overlayBackdrop} onPress={isPublishing ? undefined : onClose} />
          <MotiView
            key="publish-sheet-panel"
            from={{ translateX: SHEET_WIDTH }}
            animate={{ translateX: 0 }}
            exit={{ translateX: SHEET_WIDTH }}
            transition={{ type: "timing", duration: 220 }}
            style={styles.sheet}>
            {/* Header (varies by state) */}
            <View style={styles.header}>
              <View style={styles.headerTop}>
                <CustomText
                  text={isDone ? "Changes Published" : canPublishToLms ? `Publish to ${lmsLabel}` : "Ready to Download"}
                  useTranslationText={false}
                  size="xl"
                  weight="bold"
                  style={styles.title}
                />
                <Pressable
                  onPress={isDone ? onDone : onClose}
                  style={styles.closeButton}
                  accessibilityLabel="Close publish panel">
                  <XIcon size={18} color={colors.grey[500]} weight="regular" />
                </Pressable>
              </View>
              <CustomText
                text={
                  isDone
                    ? publishedDescription
                    : canPublishToLms
                      ? `${pluralize(items.length, "change", "changes")} will be published to your course`
                      : `${pluralize(items.length, "change", "changes")} ready to download`
                }
                useTranslationText={false}
                size="s"
                style={styles.description}
              />
            </View>

            {/* Pre-publish: warning banner. The copy escalates when any item is live. */}
            {!isDone && canPublishToLms && (
              <View style={styles.warningBanner}>
                <WarningIcon size={14} color="#F79009" weight="fill" />
                <CustomText
                  text={
                    hasLiveItems
                      ? `Some of these changes will update your live ${lmsLabel} course, which is likely being used by students right now. Enrolled students will see the updates immediately.`
                      : `These changes will be published to your live ${lmsLabel} course. Enrolled students will see the updates immediately.`
                  }
                  useTranslationText={false}
                  size="xs"
                  style={styles.warningText}
                />
              </View>
            )}
            {isError && (
              <View style={styles.errorBanner}>
                <CustomText
                  text="Publishing failed. No changes were marked as published. Please try again."
                  useTranslationText={false}
                  size="xs"
                  style={styles.errorText}
                />
              </View>
            )}

            {/* Post-publish tabs */}
            {isDone && (
              <View
                style={styles.tabRow}
                accessibilityRole="tablist"
                accessibilityLabel={tablistLabel}
                role="tablist"
                aria-label={tablistLabel}>
                <Pressable
                  onPress={() => setActiveTab("published")}
                  style={[styles.tab, activeTab === "published" && styles.tabActive]}
                  accessibilityRole="tab"
                  accessibilityState={{ selected: activeTab === "published" }}
                  accessibilityLabel={`Published (${autoPublishedItems.length})`}
                  role="tab"
                  aria-selected={activeTab === "published"}
                  aria-label={`Published (${autoPublishedItems.length})`}>
                  <CustomText
                    text="Published"
                    useTranslationText={false}
                    size="s"
                    weight="medium"
                    style={[styles.tabText, activeTab === "published" && styles.tabActiveText]}
                  />
                  <CustomText
                    text={`${autoPublishedItems.length}`}
                    useTranslationText={false}
                    size="xs"
                    style={styles.tabCount}
                  />
                </Pressable>
                <Pressable
                  onPress={() => setActiveTab("manual")}
                  style={[styles.tab, activeTab === "manual" && styles.tabActive]}
                  accessibilityRole="tab"
                  accessibilityState={{ selected: activeTab === "manual" }}
                  accessibilityLabel={`Manual Steps (${manualSteps.length})`}
                  role="tab"
                  aria-selected={activeTab === "manual"}
                  aria-label={`Manual Steps (${manualSteps.length})`}>
                  <CustomText
                    text="Manual Steps"
                    useTranslationText={false}
                    size="s"
                    weight="medium"
                    style={[styles.tabText, activeTab === "manual" && styles.tabActiveText]}
                  />
                  <CustomText
                    text={`${manualSteps.length}`}
                    useTranslationText={false}
                    size="xs"
                    style={styles.tabCount}
                  />
                </Pressable>
                <Pressable
                  onPress={() => setActiveTab("documents")}
                  style={[styles.tab, activeTab === "documents" && styles.tabActive]}
                  accessibilityRole="tab"
                  accessibilityState={{ selected: activeTab === "documents" }}
                  accessibilityLabel={`Doc Updates (${documentUpdates.length})`}
                  role="tab"
                  aria-selected={activeTab === "documents"}
                  aria-label={`Doc Updates (${documentUpdates.length})`}>
                  <CustomText
                    text="Doc Updates"
                    useTranslationText={false}
                    size="s"
                    weight="medium"
                    style={[styles.tabText, activeTab === "documents" && styles.tabActiveText]}
                  />
                  <CustomText
                    text={`${documentUpdates.length}`}
                    useTranslationText={false}
                    size="xs"
                    style={styles.tabCount}
                  />
                </Pressable>
              </View>
            )}

            {/* Changes list / Published list / Manual steps / Document updates */}
            <ScrollView
              style={styles.scrollArea}
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator>
              {!isDone && (
                <CustomText
                  text="Changes to publish"
                  useTranslationText={false}
                  size="xs"
                  weight="bold"
                  style={styles.sectionLabel}
                />
              )}

              {isDone && activeTab === "manual" ? (
                manualSteps.length > 0 ? (
                  <View style={styles.manualStepList}>
                    {manualSteps.map((step) => {
                      const isDownloadBusy = manualDownloadIds.has(step.id);
                      return (
                        <View key={step.id} style={styles.manualStepRow}>
                          <WarningIcon size={15} color="#C2410C" weight="regular" />
                          <View style={styles.manualStepContent}>
                            <CustomText
                              text={step.action}
                              useTranslationText={false}
                              size="xs"
                              weight="medium"
                              style={styles.manualStepTitle}
                            />
                            <CustomText
                              text={step.detail}
                              useTranslationText={false}
                              size="xs"
                              style={styles.manualStepDetail}
                            />
                            <View style={styles.manualStepActions}>
                              <Pressable
                                onPress={() => void handleDownloadManualStep(step)}
                                disabled={isDownloadBusy}
                                style={[
                                  styles.manualDownloadButton,
                                  isDownloadBusy && styles.manualDownloadButtonDisabled,
                                ]}
                                accessibilityRole="button"
                                accessibilityState={{ disabled: isDownloadBusy }}
                                accessibilityLabel={`Download manual step for ${step.title}`}>
                                <DownloadSimpleIcon size={14} color={colors.grey[700]} weight="regular" />
                                <CustomText
                                  text={isDownloadBusy ? "Working" : "Download"}
                                  useTranslationText={false}
                                  size="xs"
                                  weight="medium"
                                  style={styles.manualDownloadText}
                                />
                              </Pressable>
                              <Pressable
                                onPress={() => handleOpenUrl(step.externalUrl)}
                                disabled={!step.externalUrl}
                                style={[styles.manualOpenButton, !step.externalUrl && styles.manualOpenButtonDisabled]}
                                accessibilityLabel={`Open ${step.title} in ${lmsLabel}`}>
                                <ArrowSquareOutIcon size={14} color={colors.grey[700]} weight="regular" />
                                <CustomText
                                  text={`Open in ${lmsLabel}`}
                                  useTranslationText={false}
                                  size="xs"
                                  weight="medium"
                                  style={styles.manualOpenText}
                                />
                              </Pressable>
                            </View>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                ) : (
                  <View style={styles.emptyState}>
                    <CustomText
                      text="No manual steps required."
                      useTranslationText={false}
                      size="s"
                      style={styles.emptyStateText}
                    />
                  </View>
                )
              ) : isDone && activeTab === "documents" ? (
                documentUpdates.length > 0 ? (
                  <View style={styles.documentUpdateList}>
                    <View style={styles.documentUpdateIntro}>
                      <CustomText
                        text="The following documents were flagged during analysis. Download each document and upload it to the course."
                        useTranslationText={false}
                        size="xs"
                        style={styles.documentUpdateIntroText}
                      />
                    </View>
                    {documentUpdates.map((documentUpdate) => {
                      const isDownloadBusy = documentDownloadIds.has(documentUpdate.id);
                      const documentDownloadLabel = documentUpdate.downloadUrl
                        ? "Download Document"
                        : "Download Update Instructions";
                      const documentDownloadAccessibilityLabel = documentUpdate.downloadUrl
                        ? `Download document for ${documentUpdate.title}`
                        : `Download update instructions for ${documentUpdate.title}`;
                      return (
                        <View key={documentUpdate.id} style={styles.documentUpdateRow}>
                          <DownloadSimpleIcon size={16} color={colors.brand[600]} weight="regular" />
                          <View style={styles.documentUpdateContent}>
                            <CustomText
                              text={documentUpdate.title}
                              useTranslationText={false}
                              size="xs"
                              weight="medium"
                              style={styles.documentUpdateTitle}
                            />
                            <CustomText
                              text={documentUpdate.reason}
                              useTranslationText={false}
                              size="xs"
                              style={styles.documentUpdateReason}
                            />
                            <Pressable
                              onPress={() => void handleDownloadDocumentUpdate(documentUpdate)}
                              disabled={isDownloadBusy}
                              style={[
                                styles.documentDownloadButton,
                                isDownloadBusy && styles.documentDownloadButtonDisabled,
                              ]}
                              accessibilityRole="button"
                              accessibilityState={{ disabled: isDownloadBusy }}
                              accessibilityLabel={documentDownloadAccessibilityLabel}>
                              <DownloadSimpleIcon size={14} color={colors.grey[700]} weight="regular" />
                              <CustomText
                                text={isDownloadBusy ? "Working" : documentDownloadLabel}
                                useTranslationText={false}
                                size="xs"
                                weight="medium"
                                style={styles.documentDownloadText}
                              />
                            </Pressable>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                ) : (
                  <View style={styles.emptyState}>
                    <CustomText
                      text="No documents need updating."
                      useTranslationText={false}
                      size="s"
                      style={styles.emptyStateText}
                    />
                  </View>
                )
              ) : moduleGroups.length > 0 ? (
                moduleGroups.map((group) => {
                  const key = group.moduleId ?? "__course_level__";
                  const isCollapsed = collapsedModules.has(key);
                  return (
                    <View key={key}>
                      <Pressable
                        onPress={() => toggleModule(group.moduleId)}
                        style={styles.moduleHeader}
                        accessibilityLabel={`${isCollapsed ? "Expand" : "Collapse"} ${group.moduleName}`}>
                        {isCollapsed ? (
                          <CaretRightIcon size={12} color={colors.grey[500]} weight="bold" />
                        ) : (
                          <CaretDownIcon size={12} color={colors.grey[500]} weight="bold" />
                        )}
                        <StackSimpleIcon size={12} color={colors.grey[500]} weight="regular" />
                        <CustomText
                          text={group.moduleName}
                          useTranslationText={false}
                          size="xs"
                          weight="bold"
                          style={styles.moduleName}
                        />
                        <CustomText
                          text={pluralize(group.items.length, "change", "changes")}
                          useTranslationText={false}
                          size="xs"
                          style={styles.moduleCount}
                        />
                      </Pressable>
                      {!isCollapsed &&
                        group.items.map((item) => (
                          <View key={item.id} style={styles.itemRow}>
                            {isDone && (
                              <View style={styles.publishedIconBackground}>
                                <CheckCircleIcon size={14} color={colors.success[400]} weight="bold" />
                              </View>
                            )}
                            <View style={styles.itemContent}>
                              <CustomText
                                text={item.title}
                                useTranslationText={false}
                                size="xs"
                                style={styles.itemTitle}
                                numberOfLines={1}
                              />
                              <CustomText
                                text={item.category}
                                useTranslationText={false}
                                size="xs"
                                style={styles.itemCategory}
                              />
                            </View>
                            {item.wasEdited && !isDone && (
                              <CustomText
                                text="Edited"
                                useTranslationText={false}
                                size="xs"
                                weight="medium"
                                style={styles.editedBadge}
                              />
                            )}
                          </View>
                        ))}
                    </View>
                  );
                })
              ) : (
                <View style={styles.emptyState}>
                  <CustomText
                    text={isDone ? "No items were auto-published." : "No changes to publish."}
                    useTranslationText={false}
                    size="s"
                    style={styles.emptyStateText}
                  />
                </View>
              )}
            </ScrollView>

            {/* Footer (pre-publish: Publish + export; post-publish: undecided count + Done) */}
            {isDone ? (
              <View style={styles.footer}>
                <View style={styles.doneFooter}>
                  {undecidedCount > 0 && (
                    <CustomText
                      text={`${pluralize(undecidedCount, "item", "items")} still undecided`}
                      useTranslationText={false}
                      size="xs"
                      style={styles.undecidedText}
                    />
                  )}
                  <View style={styles.doneFooterActions}>
                    <View style={styles.exportWrap}>
                      {isExportMenuOpen && (
                        <View style={styles.exportMenu} accessibilityLabel="Export options">
                          <Pressable
                            onPress={() => {
                              setIsExportMenuOpen(false);
                              onExportPdf();
                            }}
                            disabled={isAnyExporting}
                            style={[styles.exportMenuItem, isAnyExporting && styles.exportMenuItemDisabled]}
                            accessibilityLabel="Download as PDF">
                            <DownloadSimpleIcon size={16} color={colors.grey[700]} weight="regular" />
                            <CustomText
                              text={isExportingPdf ? "Downloading PDF..." : "Download as PDF"}
                              useTranslationText={false}
                              size="s"
                              style={styles.exportMenuItemText}
                            />
                          </Pressable>
                          <Pressable
                            onPress={() => {
                              setIsExportMenuOpen(false);
                              onExportImscc();
                            }}
                            disabled={isAnyExporting}
                            style={[styles.exportMenuItem, isAnyExporting && styles.exportMenuItemDisabled]}
                            accessibilityLabel="Download Common Cartridge">
                            <StackSimpleIcon size={16} color={colors.grey[700]} weight="regular" />
                            <CustomText
                              text={isExportingImscc ? "Downloading IMSCC..." : "Common Cartridge (.imscc)"}
                              useTranslationText={false}
                              size="s"
                              style={styles.exportMenuItemText}
                            />
                          </Pressable>
                        </View>
                      )}
                      <Pressable
                        onPress={() => setIsExportMenuOpen((prev) => !prev)}
                        disabled={isAnyExporting}
                        style={[styles.exportButton, isAnyExporting && styles.exportButtonDisabled]}
                        accessibilityLabel="Open export options">
                        <DownloadSimpleIcon size={16} color={colors.grey[700]} weight="regular" />
                      </Pressable>
                    </View>
                    <Pressable onPress={onDone} style={styles.doneButton} accessibilityLabel="Done">
                      <CustomText
                        text="Done"
                        useTranslationText={false}
                        size="s"
                        weight="medium"
                        style={styles.doneButtonText}
                      />
                    </Pressable>
                  </View>
                </View>
              </View>
            ) : (
              <View style={styles.footer}>
                <Pressable
                  onPress={canPublish ? handlePublish : undefined}
                  disabled={!canPublish}
                  style={[styles.publishButton, !canPublish && styles.publishButtonDisabled]}
                  accessibilityLabel={
                    canPublishToLms
                      ? `Publish ${items.length} ${items.length === 1 ? "change" : "changes"}`
                      : "Publishing unavailable for uploaded course sources"
                  }
                  accessibilityState={{ disabled: !canPublish }}>
                  <PaperPlaneTiltIcon size={14} color={colors.white} weight="regular" />
                  <CustomText
                    text={
                      canPublishToLms
                        ? isPublishing
                          ? "Publishing..."
                          : `Publish ${pluralize(items.length, "change", "changes")}`
                        : "Publish Disabled"
                    }
                    useTranslationText={false}
                    size="s"
                    weight="medium"
                    style={styles.publishButtonText}
                  />
                </Pressable>
                <View style={styles.exportWrap}>
                  {isExportMenuOpen && (
                    <View style={styles.exportMenu} accessibilityLabel="Export options">
                      <Pressable
                        onPress={() => {
                          setIsExportMenuOpen(false);
                          onExportPdf();
                        }}
                        disabled={isAnyExporting}
                        style={[styles.exportMenuItem, isAnyExporting && styles.exportMenuItemDisabled]}
                        accessibilityLabel="Download as PDF">
                        <DownloadSimpleIcon size={16} color={colors.grey[700]} weight="regular" />
                        <CustomText
                          text={isExportingPdf ? "Downloading PDF..." : "Download as PDF"}
                          useTranslationText={false}
                          size="s"
                          style={styles.exportMenuItemText}
                        />
                      </Pressable>
                      <Pressable
                        onPress={() => {
                          setIsExportMenuOpen(false);
                          onExportImscc();
                        }}
                        disabled={isAnyExporting}
                        style={[styles.exportMenuItem, isAnyExporting && styles.exportMenuItemDisabled]}
                        accessibilityLabel="Download Common Cartridge">
                        <StackSimpleIcon size={16} color={colors.grey[700]} weight="regular" />
                        <CustomText
                          text={isExportingImscc ? "Downloading IMSCC..." : "Common Cartridge (.imscc)"}
                          useTranslationText={false}
                          size="s"
                          style={styles.exportMenuItemText}
                        />
                      </Pressable>
                    </View>
                  )}
                  <Pressable
                    onPress={() => setIsExportMenuOpen((prev) => !prev)}
                    disabled={isAnyExporting}
                    style={[
                      styles.exportButton,
                      !canPublishToLms && styles.exportButtonWithLabel,
                      isAnyExporting && styles.exportButtonDisabled,
                    ]}
                    accessibilityLabel="Open export options">
                    <DownloadSimpleIcon size={16} color={colors.grey[700]} weight="regular" />
                    {!canPublishToLms && (
                      <CustomText
                        text={isAnyExporting ? "Downloading" : "Download"}
                        useTranslationText={false}
                        size="s"
                        weight="medium"
                        style={styles.exportButtonText}
                      />
                    )}
                  </Pressable>
                </View>
              </View>
            )}
          </MotiView>
        </MotiView>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  overlay: {
    ...(Platform.OS === "web"
      ? ({
          position: "fixed" as unknown as "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: 1000,
        } as ViewStyle)
      : {
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        }),
  },
  overlayBackdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(16, 24, 40, 0.4)",
  },
  sheet: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: SHEET_WIDTH,
    maxWidth: "100%",
    backgroundColor: colors.white,
    flexDirection: "column",
    ...(Platform.OS === "web"
      ? ({
          boxShadow: "-8px 0 24px rgba(16, 24, 40, 0.08)",
        } as unknown as ViewStyle)
      : {
          shadowColor: "#101828",
          shadowOpacity: 0.08,
          shadowRadius: 24,
          shadowOffset: { width: -8, height: 0 },
        }),
  },
  header: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
    gap: 4,
  },
  headerTop: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  title: {
    color: colors.grey[900],
    flex: 1,
  },
  closeButton: {
    padding: 4,
    borderRadius: 6,
  },
  description: {
    color: colors.grey[700],
  },
  warningBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
    backgroundColor: "#FFFAEB",
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: colors.grey[200],
  },
  warningText: {
    color: colors.grey[700],
    flex: 1,
    lineHeight: 18,
  },
  errorBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
    backgroundColor: colors.error[50],
    borderBottomWidth: 1,
    borderBottomColor: colors.error[100],
  },
  errorText: {
    color: colors.error[700],
    lineHeight: 18,
  },
  tabRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 32,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  tab: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  tabActive: {
    borderBottomWidth: 2,
    borderBottomColor: colors.brand[600],
  },
  tabText: {
    color: colors.grey[700],
  },
  tabActiveText: {
    color: colors.brand[600],
  },
  tabCount: {
    color: colors.grey[500],
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    paddingBottom: 8,
  },
  sectionLabel: {
    color: colors.grey[700],
    paddingHorizontal: 24,
    paddingVertical: 12,
    fontSize: 12,
    letterSpacing: 0.2,
  },
  moduleHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 24,
    paddingVertical: 8,
    backgroundColor: colors.grey[50],
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: colors.grey[100],
  },
  moduleName: {
    color: colors.grey[700],
    fontSize: 11,
    flex: 1,
  },
  moduleCount: {
    color: colors.grey[500],
    fontSize: 10,
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingLeft: 40,
    paddingRight: 24,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  publishedIconBackground: {
    width: 14,
    height: 14,
    borderRadius: 999,
    backgroundColor: colors.white,
    alignItems: "center",
    justifyContent: "center",
  },
  itemContent: {
    flex: 1,
    minWidth: 0,
  },
  itemTitle: {
    color: colors.grey[900],
  },
  itemCategory: {
    color: colors.grey[500],
    fontSize: 11,
    marginTop: 2,
  },
  manualStepList: {
    borderTopWidth: 0,
  },
  manualStepRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  manualStepContent: {
    flex: 1,
    minWidth: 0,
  },
  manualStepTitle: {
    color: colors.grey[900],
    lineHeight: 18,
  },
  manualStepDetail: {
    color: colors.grey[600],
    lineHeight: 18,
    marginTop: 4,
  },
  manualStepActions: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 12,
  },
  manualDownloadButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    minHeight: 32,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
    ...(Platform.OS === "web"
      ? ({
          boxShadow: "0 1px 3px rgba(16, 24, 40, 0.14)",
        } as unknown as ViewStyle)
      : {
          shadowColor: "#101828",
          shadowOpacity: 0.14,
          shadowRadius: 3,
          shadowOffset: { width: 0, height: 1 },
        }),
  },
  manualDownloadButtonDisabled: {
    opacity: 0.5,
  },
  manualDownloadText: {
    color: colors.grey[700],
  },
  manualOpenButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    minHeight: 32,
    paddingHorizontal: 4,
  },
  manualOpenButtonDisabled: {
    opacity: 0.45,
  },
  manualOpenText: {
    color: colors.grey[700],
  },
  documentUpdateList: {
    borderTopWidth: 0,
  },
  documentUpdateIntro: {
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  documentUpdateIntroText: {
    color: colors.grey[700],
    lineHeight: 20,
  },
  documentUpdateRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  documentUpdateContent: {
    flex: 1,
    minWidth: 0,
  },
  documentUpdateTitle: {
    color: colors.grey[900],
    lineHeight: 18,
  },
  documentUpdateReason: {
    color: colors.grey[600],
    lineHeight: 18,
    marginTop: 4,
  },
  documentDownloadButton: {
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    minHeight: 32,
    marginTop: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
    ...(Platform.OS === "web"
      ? ({
          boxShadow: "0 1px 3px rgba(16, 24, 40, 0.14)",
        } as unknown as ViewStyle)
      : {
          shadowColor: "#101828",
          shadowOpacity: 0.14,
          shadowRadius: 3,
          shadowOffset: { width: 0, height: 1 },
        }),
  },
  documentDownloadButtonDisabled: {
    opacity: 0.5,
  },
  documentDownloadText: {
    color: colors.grey[700],
  },
  emptyState: {
    paddingHorizontal: 24,
    paddingVertical: 32,
    alignItems: "center",
  },
  emptyStateText: {
    color: colors.grey[500],
    textAlign: "center",
  },
  editedBadge: {
    color: colors.grey[500],
    fontSize: 10,
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  doneFooter: {
    flex: 1,
    gap: 12,
  },
  doneFooterActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  undecidedText: {
    color: colors.grey[500],
    textAlign: "center",
  },
  doneButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 12,
    height: 36,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  doneButtonText: {
    color: colors.grey[700],
  },
  publishButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingHorizontal: 12,
    height: 36,
    borderRadius: 8,
    backgroundColor: colors.brand[600],
  },
  publishButtonDisabled: {
    opacity: 0.5,
  },
  publishButtonText: {
    color: colors.white,
  },
  exportButton: {
    width: 36,
    height: 36,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  exportButtonWithLabel: {
    width: "auto",
    paddingHorizontal: 12,
  },
  exportButtonText: {
    color: colors.grey[700],
  },
  exportWrap: {
    position: "relative",
    alignItems: "flex-end",
  },
  exportMenu: {
    position: "absolute",
    right: 0,
    bottom: 44,
    minWidth: 248,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 12,
    paddingVertical: 8,
    ...(Platform.OS === "web"
      ? ({
          boxShadow: "0 8px 24px rgba(16, 24, 40, 0.12)",
        } as unknown as ViewStyle)
      : {
          shadowColor: "#101828",
          shadowOpacity: 0.12,
          shadowRadius: 12,
          shadowOffset: { width: 0, height: 6 },
        }),
  },
  exportMenuItem: {
    minHeight: 40,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  exportMenuItemDisabled: {
    opacity: 0.5,
  },
  exportMenuItemText: {
    color: colors.grey[800],
  },
  exportButtonDisabled: {
    opacity: 0.45,
  },
});

export default PublishSheet;
